import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Avatar,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Icons from './icons';
import { hasDarkModeLogo } from '../utils/configHelpers';

interface PageHeaderProps {
  marcaInfo?: {
    logo?: string;
    logoDark?: string;
    nombre?: string;
  };
  nombreMarca: string;
  companyName?: string;
  avatarUrl?: string;
  showCloseButton?: boolean;
  onCloseClick?: () => void;
  onAvatarClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onLogoClick?: () => void;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  marcaInfo,
  nombreMarca,
  companyName,
  avatarUrl,
  showCloseButton = false,
  onCloseClick,
  onAvatarClick,
  onLogoClick
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showDarkLogo = hasDarkModeLogo();

  // Avatar image loading state
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset loading state when avatarUrl changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [avatarUrl]);

  // Conditional background color: skeleton gray while loading, primary on error/no URL
  const avatarBgColor = useMemo(() => {
    if (avatarUrl && !imageLoaded && !imageError) {
      return theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e';
    }
    return theme.palette.primary.main;
  }, [avatarUrl, imageLoaded, imageError, theme.palette.mode, theme.palette.primary.main]);

  return (
    <Paper elevation={0} sx={{ backgroundColor: theme.palette.background.paper }}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ 
            py: isMobile && showCloseButton ? 1.5 : 2 
          }}
        >
          {/* Logo izquierda */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              minWidth: isMobile ? 'auto' : 200,
              cursor: onLogoClick ? 'pointer' : 'default'
            }}
            onClick={onLogoClick}
          >
            {marcaInfo?.logo ? (
              <Box sx={{ position: 'relative', height: '40px', display: 'flex', alignItems: 'center' }}>
                {/* Logo light - siempre cargado */}
                <img
                  src={marcaInfo.logo}
                  alt={nombreMarca}
                  style={{
                    height: '40px',
                    width: 'auto',
                    maxWidth: '200px',
                    objectFit: 'contain',
                    opacity: theme.palette.mode === 'light' || !showDarkLogo || !marcaInfo.logoDark ? 1 : 0,
                    transition: 'opacity 0.15s ease-in-out',
                    position: showDarkLogo && marcaInfo.logoDark ? 'absolute' : 'relative',
                    left: 0
                  }}
                />
                {/* Logo dark - solo si existe */}
                {showDarkLogo && marcaInfo.logoDark && (
                  <img
                    src={marcaInfo.logoDark}
                    alt={nombreMarca}
                    style={{
                      height: '40px',
                      width: 'auto',
                      maxWidth: '200px',
                      objectFit: 'contain',
                      opacity: theme.palette.mode === 'dark' ? 1 : 0,
                      transition: 'opacity 0.15s ease-in-out'
                    }}
                  />
                )}
              </Box>
            ) : (
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  color: theme.palette.primary.main
                }}
              >
                LukaPay
              </Typography>
            )}
          </Box>
          
          {/* Título centrado - solo en desktop */}
          {!isMobile && (
            <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography 
              sx={{ 
                fontWeight: 600,
                lineHeight: '120%',
                letterSpacing: '-0.24px',
                color: theme.palette.text.primary,
                fontSize: '24px'
              }}
            >
              <span style={{ fontWeight: 400 }}>{t('payment.title')}</span> {nombreMarca}
            </Typography>
            </Box>
          )}
          
          {/* Avatar derecha o botón de cerrar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: isMobile ? 'auto' : 200 }}>
            {showCloseButton ? (
              <IconButton
                onClick={onCloseClick}
                sx={{
                  width: isMobile ? 40 : 36,
                  height: isMobile ? 40 : 36,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: isMobile ? 24 : 20 }} />
              </IconButton>
            ) : (
              <Box 
                onClick={onAvatarClick}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  cursor: 'pointer',
                  borderRadius: '20px',
                  p: 0.5,
                  pr: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                  }
                }}
              >
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: avatarBgColor,
                    color: theme.palette.primary.contrastText,
                    fontSize: '16px',
                    fontWeight: 600,
                    transition: 'background-color 0.2s ease-in-out'
                  }}
                  imgProps={{
                    onLoad: () => setImageLoaded(true),
                    onError: () => setImageError(true)
                  }}
                >
                  {companyName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Icons className="icons-down" style={{ fontSize: 16, color: theme.palette.text.primary }} />
              </Box>
            )}
          </Box>
        </Stack>

        {/* Título para móvil removido por request del usuario */}
      </Container>
    </Paper>
  );
};

export default PageHeader;
